import { endOfDay, format, startOfDay } from 'date-fns'
import dayjs, { ConfigType, QUnitType } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import Logger from 'src/core/utils/Logger'

export { getWTZDate } from 'core-components/helpers/date'

dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)

dayjs.extend(isBetween)

const SUNDAY = 0
const SATURDAY = 6
const SERVER_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

export const toLocalFormat = (value: ConfigType) => {
  return toFormat(value, 'YYYY-MM-DD')
}

export const toFormat = (value: ConfigType, template: string) => {
  let result = ''
  if (value) {
    result = dayjs(value).format(template)
  }
  return result
}

export const dayjsUTC = (value?: ConfigType) => {
  return dayjs(value).utc()
}

export const toUTC = (value: ConfigType, formatString = 'YYYY-MM-DD') => {
  let result = ''
  if (value) {
    result = dayjs(value).utc().format(formatString)
  }
  return result
}

export const toServerFormat = (value: ConfigType) => {
  let result = ''
  if (value) {
    result = dayjs(value).utc().format(SERVER_FORMAT)
  }
  return result
}

export const toServerFormatWithoutUTC = (value: ConfigType) => toFormat(value, SERVER_FORMAT)

export const toServerDateTimeOffset = (value: Date) => {
  return format(value, 'yyyy-MM-dd HH:mm:ssXXX')
}

export const setStartDate = (date: Date | number) => {
  return startOfDay(date)
}

export const setEndDate = (date: Date | number) => {
  return endOfDay(date)
}

export const dateIsBetween = (value: ConfigType, start: ConfigType, end: ConfigType) =>
  dayjs(value).isBetween(dayjs(start), dayjs(end))

export const toStartOfTheDay = (day: Date) => {
  day.setMilliseconds(0)
  day.setSeconds(0)
  day.setMinutes(0)
  day.setHours(0)
  const fromDateTime = day.toJSON().split('.')[0].replace('T', ' ')
  return fromDateTime
}

export const getStartUtcDay = (date: Date) => {
  const copyDate = new Date(date.getTime())
  copyDate.setHours(0)
  copyDate.setMinutes(0)
  copyDate.setSeconds(0)
  return dayjs(copyDate).utc().format()
}

export function setUTCStartDate(date: Date) {
  return new Date(date.setUTCHours(0, 0, 0, 0))
}

export function setUTCEndDate(date: Date) {
  return new Date(date.setUTCHours(23, 59, 59, 0))
}

export const getFNSFormatDate = (date: Date | number | string, type: string) => {
  let result = ''

  try {
    if (!(date instanceof Date)) {
      date = new Date(date)
    }

    switch (type) {
      case 'date3':
        result = format(date, 'dd MMM yyyy') // sample 03 Feb 2020
        break
      case 'date':
        result = format(date, 'MMM do yyyy') // Mar 23th 2019
        break
      case 'full':
        result = format(date, 'MMM dd yyyy | HH:mm') // Mar 23 2019 | 18:03
        break
      default:
      // do nothing
    }
  } catch (e) {
    Logger.error(e)
  }

  return result
}

export const getFormatDate = (date: any, type: string, customTemplate?: string) => {
  const now = dayjs()
  const getTimeZone = now.utcOffset() / 60
  const oldDate = new Date(date)
  oldDate.setHours(oldDate.getHours() + getTimeZone)
  const old = dayjs(oldDate)
  switch (type) {
    case 'ago':
      return old.from(now) // sample '2 days ago'
    case 'date':
      return old.format('MMM Do YYYY') // sample 'Mar 23th 2019'
    case 'time':
      return old.format('HH:mm') // sample '18:03'
    case 'full':
      return old.format('MMM DD YYYY | HH:mm') // sample 'Mar 23 2019 | 18:03'
    case 'date2':
      return old.format('DD-MMM-YYYY') // sample
    case 'date3':
      return old.format('DD MMM YYYY') // sample 03 Feb 2020
    case 'date4':
      return old.format('DD.MM.YYYY')
    case 'date5':
      return old.format('MMM YYYY')
    case 'date6':
      return old.format('MMMM YYYY')
    case 'short':
      return old.format('DD.MM')
    case 'fullMonth':
      return old.format('MMMM Do YYYY')
    case 'custom':
      return old.format(customTemplate)
  }
  return
}

export const getDiff = (date1String: string, date2String: string, unit: QUnitType) => {
  const date1 = dayjs(date1String)
  const date2 = dayjs(date2String)
  return date1.diff(date2, unit)
}

export function isWeekend(date: Date) {
  return [SUNDAY, SATURDAY].includes(date.getDay())
}

export const getWOTZDate = (date?: string) => {
  const getTimeZone = dayjs().utcOffset() / 60
  const oldDate = date ? new Date(date) : new Date()
  oldDate.setHours(oldDate.getHours() - getTimeZone)
  return oldDate
}

export const getMinutes = (ms: number): number => Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))

export const getSeconds = (ms: number): number => Math.floor((ms % (1000 * 60)) / 1000)

export const getMilliseconds = (minutes: number) => minutes * 60 * 1000

export const addHours = (date: Date, hours: number) =>
  new Date(date.getTime() + hours * 60 * 60 * 1000)
